import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { fg } from '@confluence/feature-gating';

import { useBulkCreateContext } from '../providers/BulkCreateContextProvider';
import {
	type PageMode,
	useIssueCreateSidePanelContext,
} from '../providers/IssueCreateSidePanelContextProvider';
import { useMappingsState } from '../providers/BulkConfigureContextProvider';

type ActionType = 'createAll' | 'createSingleIssue';

export const HIGHLIGHT_ISSUE_CREATE_SOURCE = 'confluenceHighlightIssueCreate';
export const BULK_ISSUE_CREATE_SOURCE = 'confluenceBulkIssueCreate';
export const AI_BULK_ISSUE_CREATE_FEATURE_NAME = 'aiIssueCreateConfluenceBulk';

const buildAttributes = (
	baseAttributes: Record<string, any>,
	isCreatedWithAi: boolean,
	isCreatedFromTable: boolean,
	hasEditPermission: boolean,
	pageMode?: PageMode,
) => {
	baseAttributes.isCreatedFromTable = isCreatedFromTable;
	baseAttributes.isCreatedWithAi = isCreatedWithAi;
	baseAttributes.hasEditPermission = hasEditPermission;
	if (pageMode) {
		baseAttributes.pageMode = pageMode;
	}
	if (isCreatedWithAi) {
		return { ...baseAttributes, aiFeatureName: AI_BULK_ISSUE_CREATE_FEATURE_NAME };
	}
	return baseAttributes;
};

export const getSource = (isCreatedFromTable: boolean) => {
	return isCreatedFromTable ? HIGHLIGHT_ISSUE_CREATE_SOURCE : BULK_ISSUE_CREATE_SOURCE;
};

const getErrorMessage = (error: Error | unknown) => {
	if (error instanceof Error) return error.message;
	return String(error);
};

// Returns the object with number of mappings if they exist and the feature gate is active, otherwise returns object with undefined
export const useNumMappings = () => {
	const { columnToFieldMappings } = useMappingsState();
	const numMappings =
		columnToFieldMappings && fg('confluence_bulk_issue_create_additional_mappings')
			? columnToFieldMappings.length
			: undefined;

	return { numMappings };
};

export const useBulkCreateLoggingEvents = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		trackAIResultError,
		trackAIInteractionInit,
		trackAIResultView,
		trackAIResultAction,
		trackAIInteractionDismiss,
	} = useAIEventsInstrumentation();
	const [{ isCreatedWithAi, isCreatedFromTable }] = useBulkCreateContext();
	const [{ pageMode, hasEditPermission }] = useIssueCreateSidePanelContext();
	const { numMappings } = useNumMappings();

	const logMultipleIssueCreateSuccess = useCallback(
		(numIssues: number) => {
			const attributes = buildAttributes(
				{ numIssues, ...(numMappings && { numMappings }) },
				isCreatedWithAi,
				isCreatedFromTable,
				hasEditPermission,
				pageMode,
			);
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: getSource(isCreatedFromTable),
					action: 'created',
					actionSubject: 'issue',
					attributes,
				},
			}).fire();
		},
		[
			numMappings,
			createAnalyticsEvent,
			isCreatedWithAi,
			isCreatedFromTable,
			pageMode,
			hasEditPermission,
		],
	);

	const logMultipleIssueCreateFailure = useCallback(
		(numIssues: number, error: Error | unknown) => {
			const attributes = buildAttributes(
				{ error: getErrorMessage(error), numIssues },
				isCreatedWithAi,
				isCreatedFromTable,
				hasEditPermission,
				pageMode,
			);
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: getSource(isCreatedFromTable),
					action: 'failed',
					actionSubject: 'issueCreation',
					attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent, isCreatedWithAi, isCreatedFromTable, pageMode, hasEditPermission],
	);

	const logSingleIssueCreateSuccess = useCallback(() => {
		logMultipleIssueCreateSuccess(1);
	}, [logMultipleIssueCreateSuccess]);

	const logSingleIssueCreateFailure = useCallback(
		(error: Error | unknown) => {
			const attributes = buildAttributes(
				{ error: getErrorMessage(error) },
				isCreatedWithAi,
				isCreatedFromTable,
				hasEditPermission,
				pageMode,
			);
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: getSource(isCreatedFromTable),
					action: 'error',
					actionSubject: 'globalIssueCreate',
					attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent, isCreatedWithAi, isCreatedFromTable, pageMode, hasEditPermission],
	);

	const logCancelAiPanel = useCallback(() => {
		const attributes = buildAttributes(
			{},
			isCreatedWithAi,
			isCreatedFromTable,
			hasEditPermission,
			pageMode,
		);
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: getSource(isCreatedFromTable),
				action: 'cancelled',
				actionSubject: 'aiPanel',
				attributes,
			},
		}).fire();
	}, [createAnalyticsEvent, isCreatedWithAi, isCreatedFromTable, pageMode, hasEditPermission]);

	const logAiResultViewed = useCallback(
		(issuesGenerated: number) => {
			const attributes = buildAttributes(
				{ issuesGenerated },
				isCreatedWithAi,
				isCreatedFromTable,
				hasEditPermission,
				pageMode,
			);
			trackAIResultView({ attributes });
		},
		[trackAIResultView, isCreatedWithAi, isCreatedFromTable, pageMode, hasEditPermission],
	);

	const logAiResultActioned = useCallback(
		(aiResultAction: ActionType, issueId?: string) => {
			const attributes = buildAttributes(
				{},
				isCreatedWithAi,
				isCreatedFromTable,
				hasEditPermission,
				pageMode,
			);
			trackAIResultAction(aiResultAction, { attributes: { ...attributes, issueId } });
		},
		[isCreatedFromTable, isCreatedWithAi, pageMode, trackAIResultAction, hasEditPermission],
	);

	const logAiResultRemoved = useCallback(() => {
		const attributes = buildAttributes(
			{},
			isCreatedWithAi,
			isCreatedFromTable,
			hasEditPermission,
			pageMode,
		);
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: getSource(isCreatedFromTable),
				action: 'removed',
				actionSubject: 'aiResult',
				attributes,
			},
		}).fire();
	}, [createAnalyticsEvent, isCreatedFromTable, isCreatedWithAi, pageMode, hasEditPermission]);

	const logRequestFailed = useCallback(
		({ requestName, errorMessage }: { requestName: string; errorMessage: string }) => {
			const attributes = buildAttributes(
				{ errorMessage },
				isCreatedWithAi,
				isCreatedFromTable,
				hasEditPermission,
				pageMode,
			);
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: getSource(isCreatedFromTable),
					action: 'failed',
					actionSubject: requestName,
					attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent, isCreatedFromTable, isCreatedWithAi, pageMode, hasEditPermission],
	);

	const logSidePanelIssueRemoved = useCallback(() => {
		if (isCreatedWithAi) {
			logAiResultRemoved();
		}
		const attributes = buildAttributes(
			{},
			isCreatedWithAi,
			isCreatedFromTable,
			hasEditPermission,
			pageMode,
		);
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: getSource(isCreatedFromTable),
				action: 'removed',
				actionSubject: 'sidePanelIssue',
				attributes,
			},
		}).fire();
	}, [
		isCreatedWithAi,
		isCreatedFromTable,
		pageMode,
		createAnalyticsEvent,
		logAiResultRemoved,
		hasEditPermission,
	]);

	const logSidePanelIssueApproved = useCallback(
		(issueAction: ActionType) => {
			const attributes = buildAttributes(
				{ issueAction },
				isCreatedWithAi,
				isCreatedFromTable,
				hasEditPermission,
				pageMode,
			);
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: getSource(isCreatedFromTable),
					action: 'approved',
					actionSubject: 'sidePanelIssue',
					attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent, isCreatedWithAi, isCreatedFromTable, pageMode, hasEditPermission],
	);

	const logCreateAllClick = useCallback(() => {
		if (isCreatedWithAi) {
			logAiResultActioned('createAll');
		}
		logSidePanelIssueApproved('createAll');
	}, [isCreatedWithAi, logAiResultActioned, logSidePanelIssueApproved]);

	const logCreateSingleIssueClick = useCallback(
		(issueId?: string) => {
			if (isCreatedWithAi) {
				logAiResultActioned('createSingleIssue', issueId);
			}
			logSidePanelIssueApproved('createSingleIssue');
		},
		[isCreatedWithAi, logAiResultActioned, logSidePanelIssueApproved],
	);

	const logAiIssueCreationFailed = useCallback(
		(numIssues: number, error: Error | unknown = '') => {
			const attributes = buildAttributes(
				{
					errorMessage: getErrorMessage(error),
					numIssues,
				},
				isCreatedWithAi,
				isCreatedFromTable,
				hasEditPermission,
				pageMode,
			);
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: getSource(isCreatedFromTable),
					action: 'failed',
					actionSubject: 'aiIssueCreation',
					attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent, isCreatedWithAi, isCreatedFromTable, pageMode, hasEditPermission],
	);

	const logAiInteractionDismissed = useCallback(() => {
		const attributes = buildAttributes(
			{},
			isCreatedWithAi,
			isCreatedFromTable,
			hasEditPermission,
			pageMode,
		);
		trackAIInteractionDismiss({ attributes });
	}, [isCreatedFromTable, isCreatedWithAi, pageMode, trackAIInteractionDismiss, hasEditPermission]);

	const logAiNoResultsGenerated = useCallback(() => {
		const attributes = buildAttributes(
			{},
			isCreatedWithAi,
			isCreatedFromTable,
			hasEditPermission,
			pageMode,
		);
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: getSource(isCreatedFromTable),
				action: 'generated',
				actionSubject: 'aiNoResults',
				attributes,
			},
		}).fire();
	}, [createAnalyticsEvent, isCreatedFromTable, isCreatedWithAi, pageMode, hasEditPermission]);

	const logAiInteractionInitiated = useCallback(() => {
		const attributes = buildAttributes(
			{},
			isCreatedWithAi,
			isCreatedFromTable,
			hasEditPermission,
			pageMode,
		);
		trackAIInteractionInit({ attributes });
	}, [isCreatedFromTable, isCreatedWithAi, pageMode, trackAIInteractionInit, hasEditPermission]);

	const logAiStreamingBackendFailed = useCallback(
		(error: Error | unknown = '') => {
			const attributes = buildAttributes(
				{},
				isCreatedWithAi,
				isCreatedFromTable,
				hasEditPermission,
				pageMode,
			);
			trackAIResultError({ aiErrorMessage: getErrorMessage(error) }, { attributes });
		},
		[isCreatedFromTable, isCreatedWithAi, pageMode, trackAIResultError, hasEditPermission],
	);

	const logIssueCreationFailed = useCallback(
		(numIssues: number, error: Error | unknown = '') => {
			if (isCreatedWithAi) {
				logAiIssueCreationFailed(numIssues, error);
			}
			logMultipleIssueCreateFailure(numIssues, error);
		},
		[isCreatedWithAi, logAiIssueCreationFailed, logMultipleIssueCreateFailure],
	);

	const logSidePanelClosed = useCallback(() => {
		const attributes = buildAttributes(
			{},
			isCreatedWithAi,
			isCreatedFromTable,
			hasEditPermission,
			pageMode,
		);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: getSource(isCreatedFromTable),
				action: 'closed',
				actionSubject: 'modalDialog',
				actionSubjectId: 'linkCreate',
				attributes,
			},
		}).fire();
	}, [createAnalyticsEvent, isCreatedWithAi, isCreatedFromTable, pageMode, hasEditPermission]);

	return {
		logSingleIssueCreateSuccess,
		logMultipleIssueCreateSuccess,
		logSingleIssueCreateFailure,
		logCancelAiPanel,
		logCreateAllClick,
		logCreateSingleIssueClick,
		logAiInteractionInitiated,
		logAiResultViewed,
		logAiResultRemoved,
		logAiInteractionDismissed,
		logAiNoResultsGenerated,
		logRequestFailed,
		logAiStreamingBackendFailed,
		logSidePanelIssueRemoved,
		logSidePanelIssueApproved,
		logIssueCreationFailed,
		logSidePanelClosed,
	};
};
